import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  I18nManager,
  Platform,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  ToastAndroid,
  TouchableOpacity,
  View,
} from 'react-native';
import HeaderGlass from '../components/HeaderGlass';
import NeedsAttentionCard from '../components/NeedsAttentionCard';
import { DashboardSkeleton } from '../components/SkeletonLoader';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDashboardData, useInvalidateDashboard } from '../hooks/useDashboardData';
import { useRealtimeAlarms, useRealtimeDeviceStatus, useWebSocket } from '../hooks/useRealtime';
import twoFactorService from '../services/twoFactorService';
import logger from '../utils/logger';
import { platformStorage } from '../utils/storage';
import { canAccessOrgAdminScreens } from '../utils/permissions';
// P1-03: Shared UI components for state handling
import AppToast from '../components/AppToast';
import ErrorView from '../components/ErrorView';

const { width } = Dimensions.get('window');
const cardWidth = (width - 48) / 2; // 2 cards per row with margins

export default function DashboardScreen({ navigation }: any) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();

  // ✅ React Query: single hook replaces all data-fetching state
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
    isRefetching,
    dataUpdatedAt,
  } = useDashboardData(user?.orgId);
  const invalidateDashboard = useInvalidateDashboard();

  const [refreshing, setRefreshing] = useState(false);
  const [show2FAReminder, setShow2FAReminder] = useState(false);
  const [user2FAStatus, setUser2FAStatus] = useState<{
    totpEnabled: boolean;
    emailOtpEnabled: boolean;
  } | null>(null);
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    visible: false,
    message: '',
    type: 'info',
  });

  // Derived state from query data
  const stats = data?.stats ?? {
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    criticalAlarms: 0,
    warningAlarms: 0,
    totalSites: 0,
    totalZones: 0,
  };
  const recentAlarms = data?.recentAlarms ?? [];
  const sites = data?.sites ?? [];
  const lastSync = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const hasOrgAdminAccess = user?.roles ? canAccessOrgAdminScreens(user.roles) : false;

  // ✅ Personalized time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = user?.firstName || user?.email?.split('@')[0] || '';
    if (isArabic) {
      if (hour < 12) return `صباح الخير، ${name}`;
      if (hour < 17) return `مساء الخير، ${name}`;
      return `مساء الخير، ${name}`;
    }
    if (hour < 12) return `Good morning, ${name}`;
    if (hour < 17) return `Good afternoon, ${name}`;
    return `Good evening, ${name}`;
  };

  // ✅ Check if user has at least one 2FA method enabled
  const has2FAEnabled = user2FAStatus?.totpEnabled || user2FAStatus?.emailOtpEnabled;

  // ✅ Load 2FA reminder dismissed state from storage
  useEffect(() => {
    platformStorage.getItem('2fa_reminder_dismissed').then((val) => {
      if (val !== 'true') {
        setShow2FAReminder(true);
      }
    });
  }, []);

  // Connect to WebSocket for real-time updates
  const { isConnected } = useWebSocket();

  // Listen for alarm updates — invalidate React Query cache
  useRealtimeAlarms((alarm) => {
    logger.log('🔔 New alarm received:', alarm);
    invalidateDashboard();
  });

  // Listen for device status updates
  useRealtimeDeviceStatus((data) => {
    logger.log('📱 Device status changed:', data);
    invalidateDashboard();
  });

  // ✅ Load 2FA status to check if user has it enabled
  useEffect(() => {
    const load2FAStatus = async () => {
      try {
        const status = await twoFactorService.getStatus();
        setUser2FAStatus({
          totpEnabled: status.totpEnabled,
          emailOtpEnabled: status.emailOtpEnabled,
        });
      } catch (error) {
        logger.error('Failed to load 2FA status:', error);
        // Default to showing reminder if status check fails
        setUser2FAStatus({ totpEnabled: false, emailOtpEnabled: false });
      }
    };

    if (user) {
      load2FAStatus();
    }
  }, [user]);

  // Refetch on screen focus
  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [user?.orgId]),
  );

  // ✅ Smart Recommendations derived from query data
  const recommendations = useMemo(() => {
    if (!data) return [];
    const { devices } = data;
    const recs: any[] = [];

    // Critical: Low battery devices
    const lowBatteryDevices = devices.filter(
      (d: any) => d.hasBattery && d.batteryLevel && d.batteryLevel < 20,
    );
    if (lowBatteryDevices.length > 0) {
      recs.push({
        type: 'critical',
        icon: 'battery-dead',
        title: isArabic ? 'بطارية منخفضة' : 'Low Battery',
        message: isArabic
          ? `${lowBatteryDevices.length} أجهزة تحتاج شحن`
          : `${lowBatteryDevices.length} devices need charging`,
        action: () => navigateToDevices('all'),
        priority: 1,
      });
    }

    // Warning: Devices not updated for 30+ days
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const staleDevices = devices.filter(
      (d: any) => d.lastSeenAt && new Date(d.lastSeenAt) < thirtyDaysAgo,
    );
    if (staleDevices.length > 0) {
      recs.push({
        type: 'warning',
        icon: 'time',
        title: isArabic ? 'أجهزة قديمة' : 'Stale Devices',
        message: isArabic
          ? `${staleDevices.length} أجهزة لم تُحدّث منذ 30 يوم`
          : `${staleDevices.length} devices not updated for 30+ days`,
        action: () => navigateToDevices('offline'),
        priority: 2,
      });
    }

    // Info: Alarm spike detection
    const avgAlarmsPerDay = stats.criticalAlarms + stats.warningAlarms;
    if (avgAlarmsPerDay > 10) {
      recs.push({
        type: 'info',
        icon: 'trending-up',
        title: isArabic ? 'زيادة في التنبيهات' : 'Alarm Spike',
        message: isArabic
          ? `${avgAlarmsPerDay} تنبيه نشط - أعلى من المعتاد`
          : `${avgAlarmsPerDay} active alarms - higher than usual`,
        action: () => navigateToAlarms('all'),
        priority: 3,
      });
    }

    // Success: High uptime
    const uptimeRate =
      stats.totalDevices > 0 ? ((stats.onlineDevices / stats.totalDevices) * 100).toFixed(0) : '0';
    if (Number(uptimeRate) >= 90 && stats.totalDevices > 0) {
      recs.push({
        type: 'success',
        icon: 'checkmark-circle',
        title: isArabic ? 'أداء ممتاز' : 'Excellent Performance',
        message: isArabic
          ? `معدل توفر ${uptimeRate}% - استمر!`
          : `${uptimeRate}% uptime - Keep it up!`,
        action: () => {},
        priority: 4,
      });
    }

    return recs.sort((a, b) => a.priority - b.priority).slice(0, 3);
  }, [data, isArabic]);

  // ✅ Today's Snapshot derived from query data
  const todaySnapshot = useMemo(() => {
    if (!data) return null;
    const now = new Date();
    const uptimeRate =
      stats.totalDevices > 0 ? ((stats.onlineDevices / stats.totalDevices) * 100).toFixed(0) : '0';

    const trend = stats.onlineDevices > stats.offlineDevices ? 'up' : 'down';
    const trendValue =
      stats.totalDevices > 0
        ? Math.abs(
            ((stats.onlineDevices - stats.offlineDevices) / stats.totalDevices) * 100,
          ).toFixed(0)
        : '0';

    return {
      date: now,
      uptimeRate: parseInt(uptimeRate),
      trend,
      trendValue: parseInt(trendValue),
      totalAlarms: stats.criticalAlarms + stats.warningAlarms,
      totalSites: stats.totalSites,
    };
  }, [data, stats]);

  const onRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
    showToast(isArabic ? 'تم التحديث' : 'Updated');
  };

  const navigateToDevices = (filter?: 'all' | 'online' | 'offline') => {
    navigation.navigate('Devices', { filter });
  };

  const navigateToAlarms = (severity?: 'all' | 'critical' | 'warning' | string) => {
    // Map 'info' or unknown severities to 'all' since AlarmsScreen only supports 'all'|'critical'|'warning'
    const mappedSeverity = severity === 'critical' || severity === 'warning' ? severity : 'all';
    navigation.navigate('Alarms', { severity: mappedSeverity });
  };

  const navigateToSites = () => {
    navigation.navigate('SitesManagement');
  };

  const navigateToZones = () => {
    navigation.navigate('ZonesManagement');
  };

  const showToast = (message: string) => {
    if (Platform.OS === 'android') {
      ToastAndroid.show(message, ToastAndroid.SHORT);
    }

    setToast({ visible: true, message, type: 'info' });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 2200);

    logger.log('Toast:', message);
  };

  // ✅ Export Dashboard Report
  const exportDashboardReport = async () => {
    try {
      showToast(isArabic ? 'جاري التصدير...' : 'Exporting...');

      const timestamp = new Date().getTime();
      const data = [
        ['Metric', 'Value', 'Date'],
        ['Total Devices', stats.totalDevices.toString(), new Date().toLocaleString()],
        ['Online Devices', stats.onlineDevices.toString(), ''],
        ['Offline Devices', stats.offlineDevices.toString(), ''],
        ['Critical Alarms', stats.criticalAlarms.toString(), ''],
        ['Warning Alarms', stats.warningAlarms.toString(), ''],
        ['Total Sites', stats.totalSites.toString(), ''],
        ['Uptime Rate', todaySnapshot ? `${todaySnapshot.uptimeRate}%` : 'N/A', ''],
      ];

      const { exportToCSV } = await import('../utils/exportUtils');
      await exportToCSV({
        title: isArabic ? 'تقرير النظرة العامة - لوحة التحكم' : 'Dashboard Overview Report',
        headers: data[0],
        data: data.slice(1),
        filename: `dashboard_report_${timestamp}`,
      });

      showToast(isArabic ? 'تم التصدير بنجاح' : 'Exported successfully');
    } catch (error) {
      logger.error('Failed to export report:', error);
      showToast(isArabic ? 'فشل التصدير' : 'Export failed');
    }
  };

  if (isLoading && !refreshing) {
    return <DashboardSkeleton />;
  }

  // P1-03: Show error state
  if (queryError && !isLoading && !refreshing) {
    const errorMessage =
      queryError instanceof Error
        ? queryError.message
        : isArabic
        ? 'فشل تحميل لوحة التحكم'
        : 'Failed to load dashboard';
    return (
      <ErrorView
        message={errorMessage}
        onRetry={() => refetch()}
        icon="stats-chart"
        title={isArabic ? 'فشل تحميل لوحة التحكم' : 'Failed to Load Dashboard'}
      />
    );
  }

  // Calculate issues for attention card
  const attentionIssues = [];
  if (stats.criticalAlarms > 0) {
    attentionIssues.push({
      type: 'critical' as const,
      count: stats.criticalAlarms,
      action: () => navigateToAlarms('critical'),
    });
  }
  if (stats.offlineDevices > 0) {
    attentionIssues.push({
      type: 'offline' as const,
      count: stats.offlineDevices,
      action: () => navigateToDevices('offline'),
    });
  }

  // ✅ حساب حالة النظام الإجمالية
  const getSystemStatus = (): 'operational' | 'attention' | 'critical' => {
    if (stats.criticalAlarms > 0) return 'critical';
    if (stats.offlineDevices > 0) return 'attention';
    return 'operational';
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* ✅ ANDROID 15 FIX: Already using transparent + translucent */}
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      {/* Glass Header with Enhanced Status */}
      <HeaderGlass
        title={user?.organization?.name || (isArabic ? 'لوحة التحكم' : 'Dashboard')}
        subtitle={
          sites.length > 0
            ? `${sites.length} ${isArabic ? 'موقع' : 'site'}${sites.length > 1 ? 's' : ''}`
            : undefined
        }
        userName={user?.firstName || user?.email?.split('@')[0]}
        isLive={isConnected}
        lastUpdate={lastSync || undefined}
        isRTL={isRTL}
        isArabic={isArabic}
        systemStatus={getSystemStatus()}
        criticalCount={stats.criticalAlarms}
        offlineCount={stats.offlineDevices}
      />

      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.colors.primary}
            colors={[theme.colors.primary]}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* ✅ Personalized Greeting */}
        <View style={[styles.section, { marginTop: 12 }]}>
          <Text
            style={[
              styles.greetingText,
              { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {getGreeting()}
          </Text>
          <Text
            style={[
              styles.greetingSubtext,
              { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? 'إليك نظرة عامة على نظامك' : "Here's an overview of your system"}
          </Text>
        </View>

        {/* ✅ Connection Lost Banner */}
        {!isConnected && (
          <View style={[styles.section, { marginTop: 4 }]}>
            <View style={styles.connectionBanner}>
              <Ionicons name="cloud-offline" size={20} color="#ff9800" />
              <Text style={styles.connectionBannerText}>
                {isArabic
                  ? 'انقطع الاتصال المباشر — البيانات قد لا تكون محدّثة'
                  : 'Live connection lost — data may not be up to date'}
              </Text>
            </View>
          </View>
        )}

        {/* ✨ 2FA Security Reminder Banner */}
        {!has2FAEnabled && show2FAReminder && (
          <View style={[styles.section, { marginTop: 12 }]}>
            <View
              style={[
                styles.securityBanner,
                {
                  backgroundColor: theme.colors.warning + '15',
                  borderLeftColor: theme.colors.warning,
                },
              ]}
            >
              <View style={styles.securityBannerContent}>
                <View
                  style={[styles.securityIcon, { backgroundColor: theme.colors.warning + '20' }]}
                >
                  <Ionicons
                    name="shield-checkmark-outline"
                    size={28}
                    color={theme.colors.warning}
                  />
                </View>
                <View style={styles.securityTextContainer}>
                  <Text style={[styles.securityTitle, { color: theme.colors.text }]}>
                    {isArabic ? '🔐 حسّن أمان حسابك' : '🔐 Secure Your Account'}
                  </Text>
                  <Text style={[styles.securityMessage, { color: theme.colors.textSecondary }]}>
                    {isArabic
                      ? 'فعّل المصادقة الثنائية لحماية أفضل لحسابك وبياناتك'
                      : 'Enable Two-Factor Authentication for better account and data protection'}
                  </Text>
                  <View style={styles.securityActions}>
                    <TouchableOpacity
                      style={[styles.securityButton, { backgroundColor: theme.colors.warning }]}
                      onPress={() => navigation.navigate('TwoFactorSettings')}
                    >
                      <Ionicons name="settings-outline" size={16} color="#fff" />
                      <Text style={styles.securityButtonText}>
                        {isArabic ? 'فعّل الآن' : 'Enable Now'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.dismissButton}
                      onPress={async () => {
                        await platformStorage.setItem('2fa_reminder_dismissed', 'true');
                        setShow2FAReminder(false);
                      }}
                    >
                      <Text
                        style={[styles.dismissButtonText, { color: theme.colors.textSecondary }]}
                      >
                        {isArabic ? 'لاحقاً' : 'Later'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
          </View>
        )}

        {/* Needs Attention Card (Priority #1) */}
        <NeedsAttentionCard issues={attentionIssues} isRTL={isRTL} isArabic={isArabic} />

        {/* ✨ NEW: Today's Snapshot Card */}
        {todaySnapshot && (
          <View style={styles.section}>
            <View style={[styles.snapshotCard, { backgroundColor: theme.colors.card }]}>
              <View style={[styles.snapshotHeader, { flexDirection: 'row' }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.snapshotDate, { color: theme.colors.textSecondary }]}>
                    {isArabic
                      ? todaySnapshot.date.toLocaleDateString('ar-SA', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })
                      : todaySnapshot.date.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                  </Text>
                  <Text style={[styles.snapshotTitle, { color: theme.colors.text }]}>
                    {isArabic ? 'نظرة سريعة' : "Today's Snapshot"}
                  </Text>
                </View>
                <View
                  style={[
                    styles.snapshotBadge,
                    {
                      backgroundColor:
                        todaySnapshot.trend === 'up'
                          ? 'rgba(76, 175, 80, 0.15)'
                          : 'rgba(255, 152, 0, 0.15)',
                    },
                  ]}
                >
                  <Ionicons
                    name={todaySnapshot.trend === 'up' ? 'trending-up' : 'trending-down'}
                    size={24}
                    color={todaySnapshot.trend === 'up' ? '#4caf50' : '#ff9800'}
                  />
                  <Text
                    style={[
                      styles.snapshotTrend,
                      { color: todaySnapshot.trend === 'up' ? '#4caf50' : '#ff9800' },
                    ]}
                  >
                    {todaySnapshot.trend === 'up' ? '+' : ''}
                    {todaySnapshot.trendValue}%
                  </Text>
                </View>
              </View>

              <View style={[styles.snapshotStats, { flexDirection: 'row' }]}>
                {hasOrgAdminAccess && (
                  <View style={styles.snapshotStat}>
                    <Text style={[styles.snapshotStatValue, { color: '#4caf50' }]}>
                      {todaySnapshot.totalSites}
                    </Text>
                    <Text style={[styles.snapshotStatLabel, { color: theme.colors.textSecondary }]}>
                      {isArabic ? 'مواقع' : 'Sites'}
                    </Text>
                  </View>
                )}

                <View style={styles.snapshotStat}>
                  <Text style={[styles.snapshotStatValue, { color: theme.colors.primary }]}>
                    {todaySnapshot.uptimeRate}%
                  </Text>
                  <Text style={[styles.snapshotStatLabel, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'معدل التوفر' : 'Uptime'}
                  </Text>
                  <View style={styles.uptimeBar}>
                    <View
                      style={[
                        styles.uptimeBarFill,
                        {
                          width: `${todaySnapshot.uptimeRate}%` as any,
                          backgroundColor:
                            todaySnapshot.uptimeRate >= 90
                              ? '#4caf50'
                              : todaySnapshot.uptimeRate >= 70
                              ? '#ff9800'
                              : '#f44336',
                        },
                      ]}
                    />
                  </View>
                </View>

                {hasOrgAdminAccess && (
                  <View style={styles.snapshotStat}>
                    <Text style={[styles.snapshotStatValue, { color: '#9c27b0' }]}>
                      {stats.totalZones}
                    </Text>
                    <Text style={[styles.snapshotStatLabel, { color: theme.colors.textSecondary }]}>
                      {isArabic ? 'مناطق' : 'Zones'}
                    </Text>
                  </View>
                )}

                <View style={styles.snapshotStat}>
                  <Text
                    style={[
                      styles.snapshotStatValue,
                      {
                        color:
                          todaySnapshot.totalAlarms > 5
                            ? '#ff5252'
                            : todaySnapshot.totalAlarms > 0
                            ? '#ff9800'
                            : '#4caf50',
                      },
                    ]}
                  >
                    {todaySnapshot.totalAlarms}
                  </Text>
                  <Text style={[styles.snapshotStatLabel, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'تنبيهات' : 'Alarms'}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        )}

        {/* ✨ NEW: Smart Recommendations */}
        {recommendations.length > 0 && (
          <View style={styles.section}>
            <View style={[styles.sectionHeader, { flexDirection: 'row' }]}>
              <Ionicons name="bulb" size={20} color={theme.colors.primary} />
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.colors.text,
                    textAlign: isRTL ? 'right' : 'left',
                    marginLeft: isRTL ? 0 : 8,
                    marginRight: isRTL ? 8 : 0,
                  },
                ]}
              >
                {isArabic ? 'توصيات ذكية' : 'Smart Recommendations'}
              </Text>
            </View>

            <View style={styles.recommendationsList}>
              {recommendations.map((rec, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.recommendationCard,
                    {
                      backgroundColor: theme.colors.card,
                      borderLeftWidth: isRTL ? 0 : 4,
                      borderRightWidth: isRTL ? 4 : 0,
                      borderLeftColor:
                        rec.type === 'critical'
                          ? '#ff5252'
                          : rec.type === 'warning'
                          ? '#ff9800'
                          : rec.type === 'success'
                          ? '#4caf50'
                          : theme.colors.primary,
                      borderRightColor:
                        rec.type === 'critical'
                          ? '#ff5252'
                          : rec.type === 'warning'
                          ? '#ff9800'
                          : rec.type === 'success'
                          ? '#4caf50'
                          : theme.colors.primary,
                    },
                  ]}
                  onPress={rec.action}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.recommendationIcon,
                      {
                        backgroundColor:
                          rec.type === 'critical'
                            ? 'rgba(255, 82, 82, 0.15)'
                            : rec.type === 'warning'
                            ? 'rgba(255, 152, 0, 0.15)'
                            : rec.type === 'success'
                            ? 'rgba(76, 175, 80, 0.15)'
                            : 'rgba(63, 81, 181, 0.15)',
                      },
                    ]}
                  >
                    <Ionicons
                      name={rec.icon}
                      size={24}
                      color={
                        rec.type === 'critical'
                          ? '#ff5252'
                          : rec.type === 'warning'
                          ? '#ff9800'
                          : rec.type === 'success'
                          ? '#4caf50'
                          : theme.colors.primary
                      }
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={[
                        styles.recommendationTitle,
                        { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {rec.title}
                    </Text>
                    <Text
                      style={[
                        styles.recommendationMessage,
                        { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {rec.message}
                    </Text>
                  </View>
                  <Ionicons
                    name={isRTL ? 'chevron-back' : 'chevron-forward'}
                    size={20}
                    color={theme.colors.textSecondary}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* KPIs Section - Smart Priority Order */}
        <View style={styles.section}>
          <Text
            style={[
              styles.sectionTitle,
              { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? 'المؤشرات الرئيسية' : 'Key Metrics'}
          </Text>

          <View style={[styles.kpiGrid, { flexDirection: 'row' }]}>
            {/* Priority Order: Critical → Offline → Online → Total */}

            {/* Critical Alarms - Highest Priority */}
            <TouchableOpacity
              style={[
                styles.kpiCard,
                styles.kpiCritical,
                { backgroundColor: theme.colors.card },
                stats.criticalAlarms > 0 && styles.kpiElevated,
              ]}
              onPress={() => navigateToAlarms('critical')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'تنبيهات حرجة' : 'Critical alarms'}, ${
                stats.criticalAlarms
              }`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لعرض التنبيهات الحرجة'
                  : 'Double tap to view critical alarms'
              }
              accessibilityRole="button"
            >
              <View style={[styles.kpiIcon, styles.kpiIconCritical]}>
                <Ionicons name="alert-circle" size={28} color="#ff5252" />
              </View>
              <Text style={[styles.kpiValue, styles.kpiValueCritical]}>{stats.criticalAlarms}</Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {isArabic ? 'حرجة' : 'Critical'}
              </Text>
            </TouchableOpacity>

            {/* Warning Alarms */}
            <TouchableOpacity
              style={[
                styles.kpiCard,
                stats.warningAlarms > 0 ? styles.kpiWarningBorder : {},
                { backgroundColor: theme.colors.card },
              ]}
              onPress={() => navigateToAlarms('warning')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'تنبيهات تحذير' : 'Warning alarms'}, ${
                stats.warningAlarms
              }`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لعرض التنبيهات التحذيرية'
                  : 'Double tap to view warning alarms'
              }
              accessibilityRole="button"
            >
              <View style={[styles.kpiIcon, styles.kpiIconWarning]}>
                <Ionicons name="warning" size={28} color="#ff9800" />
              </View>
              <Text style={[styles.kpiValue, { color: '#ff9800' }]}>{stats.warningAlarms}</Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {isArabic ? 'تحذير' : 'Warning'}
              </Text>
            </TouchableOpacity>

            {/* Online Devices */}
            <TouchableOpacity
              style={[styles.kpiCard, { backgroundColor: theme.colors.card }]}
              onPress={() => navigateToDevices('online')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'أجهزة متصلة' : 'Online devices'}, ${
                stats.onlineDevices
              }`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لعرض الأجهزة المتصلة'
                  : 'Double tap to view online devices'
              }
              accessibilityRole="button"
            >
              <View style={[styles.kpiIcon, styles.kpiIconOnline]}>
                <Ionicons name="checkmark-circle" size={28} color="#4caf50" />
              </View>
              <Text style={[styles.kpiValue, { color: '#4caf50' }]}>{stats.onlineDevices}</Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {isArabic ? 'متصل' : 'Online'}
              </Text>
            </TouchableOpacity>

            {/* Offline Devices */}
            <TouchableOpacity
              style={[
                styles.kpiCard,
                { backgroundColor: theme.colors.card },
                stats.offlineDevices > 0 && styles.kpiOfflineBorder,
              ]}
              onPress={() => navigateToDevices('offline')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'أجهزة غير متصلة' : 'Offline devices'}, ${
                stats.offlineDevices
              }`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لعرض الأجهزة غير المتصلة'
                  : 'Double tap to view offline devices'
              }
              accessibilityRole="button"
            >
              <View style={[styles.kpiIcon, styles.kpiIconOffline]}>
                <Ionicons
                  name="close-circle"
                  size={28}
                  color={stats.offlineDevices > 0 ? '#f44336' : '#9e9e9e'}
                />
              </View>
              <Text
                style={[
                  styles.kpiValue,
                  { color: stats.offlineDevices > 0 ? '#f44336' : theme.colors.text },
                ]}
              >
                {stats.offlineDevices}
              </Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {isArabic ? 'غير متصل' : 'Offline'}
              </Text>
            </TouchableOpacity>

            {/* Total Devices */}
            <TouchableOpacity
              style={[styles.kpiCard, { backgroundColor: theme.colors.card }]}
              onPress={() => navigateToDevices('all')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'إجمالي الأجهزة' : 'Total devices'}, ${
                stats.totalDevices
              }`}
              accessibilityHint={
                isArabic ? 'انقر نقرًا مزدوجًا لعرض جميع الأجهزة' : 'Double tap to view all devices'
              }
              accessibilityRole="button"
            >
              <View style={[styles.kpiIcon, styles.kpiIconTotal]}>
                <Ionicons name="grid" size={28} color={theme.colors.primary} />
              </View>
              <Text style={[styles.kpiValue, { color: theme.colors.primary }]}>
                {stats.totalDevices}
              </Text>
              <Text style={[styles.kpiLabel, { color: theme.colors.textSecondary }]}>
                {isArabic ? 'إجمالي' : 'Total'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Recent Alarms - Last 3 Only */}
        {recentAlarms.length > 0 && (
          <View style={styles.section}>
            <View style={[styles.sectionHeader, { flexDirection: 'row' }]}>
              <Text
                style={[
                  styles.sectionTitle,
                  { color: theme.colors.text, flex: 1, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {isArabic ? 'آخر التنبيهات' : 'Recent Alarms'}
              </Text>
              <TouchableOpacity
                onPress={() => navigateToAlarms('all')}
                // ✅ P1-06: Accessibility
                accessible={true}
                accessibilityLabel={isArabic ? 'عرض جميع التنبيهات' : 'View all alarms'}
                accessibilityHint={
                  isArabic
                    ? 'انقر نقرًا مزدوجًا لعرض جميع التنبيهات'
                    : 'Double tap to view all alarms'
                }
                accessibilityRole="button"
              >
                <Text style={[styles.seeAll, { color: theme.colors.primary }]}>
                  {isArabic ? 'عرض الكل' : 'See all'}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.alarmsList}>
              {recentAlarms.slice(0, 2).map((alarm, index) => {
                // ✅ P1-06: Build accessibility label for alarm card
                const severityLabel =
                  alarm.severity === 'critical'
                    ? isArabic
                      ? 'تنبيه حرج'
                      : 'Critical alarm'
                    : isArabic
                    ? 'تنبيه تحذير'
                    : 'Warning alarm';
                const deviceName =
                  alarm.deviceName || alarm.device?.name || (isArabic ? 'تنبيه' : 'Alert');
                const timeAgo = getTimeAgo(alarm.createdAt, isArabic);
                const accessibilityLabel = `${severityLabel}, ${deviceName}, ${timeAgo}`;

                return (
                  <TouchableOpacity
                    key={alarm.id || index}
                    style={[
                      styles.alarmCard,
                      {
                        backgroundColor: theme.colors.card,
                        borderRightWidth: isRTL ? 4 : 0,
                        borderLeftWidth: isRTL ? 0 : 4,
                        borderColor: alarm.severity === 'critical' ? '#ff5252' : '#ff9800',
                      },
                    ]}
                    onPress={() => navigateToAlarms(alarm.severity)}
                    activeOpacity={0.7}
                    // ✅ P1-06: Accessibility
                    accessible={true}
                    accessibilityLabel={accessibilityLabel}
                    accessibilityHint={
                      isArabic
                        ? 'انقر نقرًا مزدوجًا لعرض تفاصيل التنبيه'
                        : 'Double tap to view alarm details'
                    }
                    accessibilityRole="button"
                  >
                    <View
                      style={[
                        styles.alarmIcon,
                        alarm.severity === 'critical'
                          ? styles.alarmIconCritical
                          : styles.alarmIconWarning,
                      ]}
                    >
                      <Ionicons
                        name={alarm.severity === 'critical' ? 'alert-circle' : 'warning'}
                        size={26}
                        color={alarm.severity === 'critical' ? '#ff5252' : '#ff9800'}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text
                        style={[
                          styles.alarmTitle,
                          { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                        ]}
                        numberOfLines={1}
                      >
                        {alarm.deviceName || alarm.device?.name || (isArabic ? 'تنبيه' : 'Alert')}
                      </Text>
                      {alarm.message && (
                        <Text
                          style={[
                            styles.alarmMessage,
                            {
                              color: alarm.severity === 'critical' ? '#ff5252' : '#ff9800',
                              textAlign: isRTL ? 'right' : 'left',
                            },
                          ]}
                          numberOfLines={1}
                        >
                          {alarm.message}
                        </Text>
                      )}
                      <Text
                        style={[
                          styles.alarmSite,
                          {
                            color: theme.colors.textSecondary,
                            textAlign: isRTL ? 'right' : 'left',
                          },
                        ]}
                      >
                        {alarm.device?.site?.name || (isArabic ? 'غير محدد' : 'Unspecified')}
                      </Text>
                    </View>
                    <Text style={[styles.alarmTime, { color: theme.colors.textSecondary }]}>
                      {getTimeAgo(alarm.createdAt, isArabic)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {/* Quick Actions */}
        <View style={styles.section}>
          <Text
            style={[
              styles.sectionTitle,
              { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? 'إجراءات سريعة' : 'Quick Actions'}
          </Text>

          <View style={[styles.actionsGrid, { flexDirection: 'row' }]}>
            {/* Alarms */}
            <TouchableOpacity
              style={[
                styles.actionCard,
                {
                  backgroundColor:
                    stats.criticalAlarms > 0 ? 'rgba(255,82,82,0.08)' : theme.colors.card,
                },
                stats.criticalAlarms > 0 && {
                  borderWidth: 1.5,
                  borderColor: 'rgba(255,82,82,0.35)',
                },
              ]}
              onPress={() => navigateToAlarms('all')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={
                stats.criticalAlarms > 0
                  ? `${isArabic ? 'التنبيهات' : 'Alarms'}, ${stats.criticalAlarms} ${
                      isArabic ? 'حرج' : 'critical'
                    }`
                  : isArabic
                  ? 'التنبيهات'
                  : 'Alarms'
              }
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لعرض جميع التنبيهات'
                  : 'Double tap to view all alarms'
              }
              accessibilityRole="button"
            >
              <View
                style={[
                  styles.actionIcon,
                  {
                    backgroundColor: 'rgba(255,82,82,0.12)',
                    borderWidth: 1.5,
                    borderColor: 'rgba(255,82,82,0.25)',
                  },
                ]}
              >
                <Ionicons name="notifications" size={24} color="#ff5252" />
              </View>
              <Text style={[styles.actionLabel, { color: theme.colors.text }]}>
                {isArabic ? 'التنبيهات' : 'Alarms'}
              </Text>
              {stats.criticalAlarms > 0 && (
                <View style={styles.actionBadge}>
                  <Text style={styles.actionBadgeText}>{stats.criticalAlarms}</Text>
                </View>
              )}
            </TouchableOpacity>

            {/* Devices */}
            <TouchableOpacity
              style={[styles.actionCard, { backgroundColor: theme.colors.card }]}
              onPress={() => navigateToDevices('all')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={isArabic ? 'الأجهزة' : 'Devices'}
              accessibilityHint={
                isArabic ? 'انقر نقرًا مزدوجًا لعرض جميع الأجهزة' : 'Double tap to view all devices'
              }
              accessibilityRole="button"
            >
              <View
                style={[
                  styles.actionIcon,
                  {
                    backgroundColor: 'rgba(63,81,181,0.12)',
                    borderWidth: 1.5,
                    borderColor: 'rgba(63,81,181,0.25)',
                  },
                ]}
              >
                <Ionicons name="hardware-chip" size={24} color={theme.colors.primary} />
              </View>
              <Text style={[styles.actionLabel, { color: theme.colors.text }]}>
                {isArabic ? 'الأجهزة' : 'Devices'}
              </Text>
            </TouchableOpacity>

            {hasOrgAdminAccess && (
              <>
                {/* Sites */}
                <TouchableOpacity
                  style={[styles.actionCard, { backgroundColor: theme.colors.card }]}
                  onPress={navigateToSites}
                  activeOpacity={0.7}
                  accessible={true}
                  accessibilityLabel={isArabic ? 'المواقع' : 'Sites'}
                  accessibilityHint={
                    isArabic
                      ? 'انقر نقرًا مزدوجًا لعرض جميع المواقع'
                      : 'Double tap to view all sites'
                  }
                  accessibilityRole="button"
                >
                  <View
                    style={[
                      styles.actionIcon,
                      {
                        backgroundColor: 'rgba(0,188,212,0.12)',
                        borderWidth: 1.5,
                        borderColor: 'rgba(0,188,212,0.25)',
                      },
                    ]}
                  >
                    <Ionicons name="business" size={24} color="#00bcd4" />
                  </View>
                  <Text style={[styles.actionLabel, { color: theme.colors.text }]}>
                    {isArabic ? 'المواقع' : 'Sites'}
                  </Text>
                </TouchableOpacity>

                {/* Zones */}
                <TouchableOpacity
                  style={[styles.actionCard, { backgroundColor: theme.colors.card }]}
                  onPress={navigateToZones}
                  activeOpacity={0.7}
                  accessible={true}
                  accessibilityLabel={`${isArabic ? 'المناطق' : 'Zones'}, ${stats.totalZones}`}
                  accessibilityHint={
                    isArabic
                      ? 'انقر نقرًا مزدوجًا لعرض جميع المناطق'
                      : 'Double tap to view all zones'
                  }
                  accessibilityRole="button"
                >
                  <View
                    style={[
                      styles.actionIcon,
                      {
                        backgroundColor: 'rgba(156,39,176,0.12)',
                        borderWidth: 1.5,
                        borderColor: 'rgba(156,39,176,0.25)',
                      },
                    ]}
                  >
                    <Ionicons name="map" size={24} color="#9c27b0" />
                  </View>
                  <Text style={[styles.actionLabel, { color: theme.colors.text }]}>
                    {isArabic ? 'المناطق' : 'Zones'}
                  </Text>
                  {stats.totalZones > 0 && (
                    <View
                      style={[
                        styles.actionBadge,
                        { backgroundColor: '#9c27b0', borderColor: theme.colors.card },
                      ]}
                    >
                      <Text style={styles.actionBadgeText}>{stats.totalZones}</Text>
                    </View>
                  )}
                </TouchableOpacity>

                {/* Export Report */}
                <TouchableOpacity
                  style={[styles.actionCard, { backgroundColor: theme.colors.card }]}
                  onPress={exportDashboardReport}
                  activeOpacity={0.7}
                  accessible={true}
                  accessibilityLabel={isArabic ? 'تصدير تقرير' : 'Export report'}
                  accessibilityHint={
                    isArabic
                      ? 'انقر نقرًا مزدوجًا لتصدير تقرير لوحة التحكم'
                      : 'Double tap to export dashboard report'
                  }
                  accessibilityRole="button"
                >
                  <View
                    style={[
                      styles.actionIcon,
                      {
                        backgroundColor: 'rgba(76,175,80,0.12)',
                        borderWidth: 1.5,
                        borderColor: 'rgba(76,175,80,0.25)',
                      },
                    ]}
                  >
                    <Ionicons name="download" size={24} color="#4caf50" />
                  </View>
                  <Text style={[styles.actionLabel, { color: theme.colors.text }]}>
                    {isArabic ? 'تصدير' : 'Export'}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>

        {/* Bottom Padding */}
        <View style={{ height: 40 }} />
      </ScrollView>

      <AppToast visible={toast.visible} message={toast.message} type={toast.type} bottom={16} />
    </View>
  );
}

// Helper function for relative time with better Arabic support
function getTimeAgo(dateString: string, isArabic: boolean = false): string {
  const date = new Date(dateString);
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

  if (seconds < 60) return isArabic ? 'الآن' : 'Now';
  if (seconds < 120) return isArabic ? 'منذ دقيقة' : '1 min';
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    return isArabic ? `منذ ${mins} د` : `${mins} min`;
  }
  if (seconds < 7200) return isArabic ? 'منذ ساعة' : '1 hour';
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return isArabic ? `منذ ${hours} س` : `${hours} hrs`;
  }
  const days = Math.floor(seconds / 86400);
  return isArabic ? `منذ ${days} يوم` : `${days} days`;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 20,
    paddingTop: 50,
    paddingBottom: 30,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  greeting: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    marginBottom: 4,
  },
  userName: {
    fontSize: 28,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 4,
  },
  orgName: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  exportBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  refreshBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  statCard: {
    width: cardWidth,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 13,
    textAlign: 'center',
  },
  warningsSection: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
  },
  seeAllText: {
    fontSize: 14,
    fontWeight: '600',
  },
  warningItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingLeft: 12,
    borderLeftWidth: 3,
    gap: 12,
  },
  warningText: {
    flex: 1,
    fontSize: 15,
  },
  alarmsSection: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  orgRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  wsStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  wsDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  wsText: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.95)',
    fontWeight: '600',
  },
  lastSyncText: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.75)',
    marginTop: 4,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  actionCard: {
    width: cardWidth,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  actionCardTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    marginTop: 8,
    marginBottom: 4,
  },
  actionCardSubtitle: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 12,
    textAlign: 'center',
  },
  alarmItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
  },
  alarmSeverityDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  alarmContent: {
    flex: 1,
  },
  alarmTitle: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 2,
  },
  alarmDevice: {
    fontSize: 13,
  },
  alarmTime: {
    fontSize: 12,
  },
  quickActions: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },

  // New Premium Styles
  kpiGrid: {
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
  },
  kpiCard: {
    width: (width - 48) / 2,
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  kpiElevated: {
    elevation: 6,
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  kpiCritical: {
    borderWidth: 1.5,
    borderColor: 'rgba(255, 82, 82, 0.3)',
  },
  kpiWarning: {
    borderWidth: 1,
    borderColor: 'rgba(158, 158, 158, 0.2)',
  },
  kpiWarningBorder: {
    borderWidth: 1.5,
    borderColor: 'rgba(255, 152, 0, 0.35)',
  },
  kpiOfflineBorder: {
    borderWidth: 1.5,
    borderColor: 'rgba(244, 67, 54, 0.35)',
  },
  kpiIconWarning: {
    backgroundColor: 'rgba(255, 152, 0, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255, 152, 0, 0.3)',
  },
  uptimeBar: {
    height: 4,
    width: '100%',
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: 2,
    marginTop: 6,
    overflow: 'hidden',
  },
  uptimeBarFill: {
    height: 4,
    borderRadius: 2,
  },
  kpiIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  kpiIconCritical: {
    backgroundColor: 'rgba(255, 82, 82, 0.15)',
    borderWidth: 2,
    borderColor: 'rgba(255, 82, 82, 0.3)',
  },
  kpiIconOffline: {
    backgroundColor: 'rgba(158, 158, 158, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(158, 158, 158, 0.2)',
  },
  kpiIconOnline: {
    backgroundColor: 'rgba(76, 175, 80, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(76, 175, 80, 0.3)',
  },
  kpiIconTotal: {
    backgroundColor: 'rgba(63, 81, 181, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(63, 81, 181, 0.3)',
  },
  kpiValue: {
    fontSize: 28,
    fontWeight: '800',
    marginBottom: 4,
    letterSpacing: -0.5,
  },
  kpiValueCritical: {
    color: '#ff5252',
  },
  kpiLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    opacity: 0.85,
  },
  alarmsList: {
    gap: 10,
  },
  alarmCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 2,
  },
  alarmIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  alarmIconCritical: {
    backgroundColor: 'rgba(255, 82, 82, 0.15)',
    borderWidth: 2,
    borderColor: 'rgba(255, 82, 82, 0.3)',
  },
  alarmIconWarning: {
    backgroundColor: 'rgba(255, 152, 0, 0.15)',
    borderWidth: 1.5,
    borderColor: 'rgba(255, 152, 0, 0.3)',
  },
  alarmSite: {
    fontSize: 12,
    fontWeight: '400',
  },
  alarmMessage: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 2,
  },
  greetingText: {
    fontSize: 22,
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  greetingSubtext: {
    fontSize: 14,
    fontWeight: '400',
    marginTop: 4,
    opacity: 0.8,
  },
  connectionBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255, 152, 0, 0.3)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 10,
  },
  connectionBannerText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '500',
    color: '#ff9800',
  },
  actionsGrid: {
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
  },
  actionHighlight: {
    borderWidth: 2,
    borderColor: 'rgba(63, 81, 181, 0.3)',
    elevation: 4,
  },
  actionIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  actionIconHighlight: {
    backgroundColor: 'rgba(63, 81, 181, 0.15)',
    borderWidth: 2,
    borderColor: 'rgba(63, 81, 181, 0.3)',
  },
  actionIconDefault: {
    backgroundColor: 'rgba(158, 158, 158, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(158, 158, 158, 0.15)',
  },
  actionLabel: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  actionBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: '#ff5252',
    borderRadius: 14,
    minWidth: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    borderWidth: 2,
    borderColor: '#ffffff',
    shadowColor: '#ff5252',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.4,
    shadowRadius: 4,
    elevation: 4,
  },
  actionBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '800',
  },
  section: {
    paddingHorizontal: 16,
    marginTop: 20,
  },
  seeAll: {
    fontSize: 14,
    fontWeight: '600',
  },

  // ✨ NEW: Today's Snapshot Styles
  snapshotCard: {
    borderRadius: 16,
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  snapshotHeader: {
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  snapshotDate: {
    fontSize: 12,
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  snapshotTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  snapshotBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 6,
  },
  snapshotTrend: {
    fontSize: 16,
    fontWeight: '700',
  },
  snapshotStats: {
    justifyContent: 'space-around',
    gap: 16,
  },
  snapshotStat: {
    flex: 1,
    alignItems: 'center',
  },
  snapshotStatValue: {
    fontSize: 28,
    fontWeight: '800',
    marginBottom: 4,
  },
  snapshotStatLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // ✨ NEW: Smart Recommendations Styles
  recommendationsList: {
    gap: 12,
    marginTop: 8,
  },
  recommendationCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 2,
  },
  recommendationIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  recommendationMessage: {
    fontSize: 14,
    lineHeight: 20,
  },

  // ✨ Security Reminder Banner Styles
  securityBanner: {
    borderRadius: 16,
    padding: 16,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  securityBannerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
  },
  securityIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  securityTextContainer: {
    flex: 1,
  },
  securityTitle: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 6,
  },
  securityMessage: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 14,
  },
  securityActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  securityButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 10,
    gap: 8,
  },
  securityButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  dismissButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  dismissButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  toastContainer: {
    position: 'absolute',
    left: 16,
    right: 16,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    zIndex: 1000,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.2)',
  },
  toastText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
});
